import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  CreditCard, Users, TrendingUp, CheckCircle2, AlertCircle, RefreshCw, Clock, Globe, X,
  FileText, Shield, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [currencyInfo, setCurrencyInfo] = useState(null);
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false);
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  const [contractPlans, setContractPlans] = useState(null);
  const [contractStatus, setContractStatus] = useState(null);
  const [selectingContract, setSelectingContract] = useState(false);

  useEffect(() => { fetchDashboard(); fetchCurrency(); fetchContractData(); }, []);

  const fetchCurrency = async () => {
    try {
      const { data: c } = await axios.get(`${API_URL}/api/currency/detect`, { withCredentials: true });
      setCurrencyInfo(c);
    } catch { /* fallback to USD */ }
  };

  const fetchAllCurrencies = async () => {
    try {
      const { data: r } = await axios.get(`${API_URL}/api/currency/rates`, { withCredentials: true });
      setAllCurrencies(r.currencies || []);
      setShowCurrencyPicker(true);
    } catch { toast.error('Failed to load currencies'); }
  };

  const setCurrency = async (code) => {
    try {
      const { data: r } = await axios.put(`${API_URL}/api/currency/set`, { currency: code }, { withCredentials: true });
      setCurrencyInfo(r);
      setShowCurrencyPicker(false);
      toast.success(`Currency set to ${r.currency}`);
    } catch { toast.error('Failed to set currency'); }
  };

  const formatPrice = (usdPrice) => {
    if (!currencyInfo || currencyInfo.currency === 'USD') return `$${usdPrice.toFixed(2)}`;
    const converted = usdPrice * currencyInfo.rate;
    return `${currencyInfo.symbol}${converted.toFixed(2)}`;
  };

  const cancelTrial = async () => {
    if (!window.confirm('Are you sure you want to cancel your trial? You can continue using the platform until the trial period ends.')) return;
    setCancelling(true);
    try {
      const { data: r } = await axios.post(`${API_URL}/api/subscription/cancel-trial`, {}, { withCredentials: true });
      toast.success(r.message);
      fetchDashboard();
      fetchContractData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel trial');
    } finally { setCancelling(false); }
  };

  const fetchContractData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscription/contract-plans`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/subscription/contract-status`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setContractPlans(plansRes.data);
      setContractStatus(statusRes.data);
    } catch { /* ignore */ }
  };

  const selectContract = async (planId) => {
    setSelectingContract(true);
    try {
      const { data: r } = await axios.post(`${API_URL}/api/subscription/select-contract`, {
        plan_id: planId,
        origin_url: window.location.origin
      }, { withCredentials: true });
      if (r.checkout_url) {
        toast.success('Redirecting to payment...');
        window.location.href = r.checkout_url;
        return;
      }
      toast.success(r.message);
      fetchDashboard();
      fetchContractData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to select contract');
    } finally { setSelectingContract(false); }
  };

  const cancelRenewal = async () => {
    if (!window.confirm('Are you sure you want to cancel auto-renewal? Your contract will not renew at the end of the current term.')) return;
    try {
      const { data: r } = await axios.post(`${API_URL}/api/subscription/cancel-renewal`, {}, { withCredentials: true });
      toast.success(r.message);
      fetchContractData();
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel renewal');
    }
  };

  const fetchDashboard = async () => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/subscription/dashboard`, { withCredentials: true });
      setData(d);
    } catch (err) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const toggleRenewal = async () => {
    try {
      const { data: result } = await axios.put(`${API_URL}/api/subscription/toggle-renewal`, {}, { withCredentials: true });
      toast.success(result.message);
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to toggle auto-renewal');
    }
  };

  const handleUpgrade = async (tierId) => {
    setUpgrading(true);
    try {
      const { data: result } = await axios.post(`${API_URL}/api/payments/checkout`, {
        tier_id: tierId,
        payment_method: 'stripe',
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      toast.error('Failed to start checkout');
    } finally {
      setUpgrading(false);
    }
  };

  const tiers = [
    { id: 'tier_1_10', name: 'Starter', range: '1-10', price: 120.99 },
    { id: 'tier_11_50', name: 'Growth', range: '11-50', price: 169.99 },
    { id: 'tier_51_100', name: 'Professional', range: '51-100', price: 200.00 },
    { id: 'tier_100_plus', name: 'Enterprise', range: '100+', price: 239.00 },
  ];

  const content = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="spinner w-8 h-8" />
        </div>
      );
    }

    if (!data) {
      return (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <AlertCircle className="w-10 h-10 mb-3 text-destructive" />
          <p>Failed to load subscription data</p>
          <Button variant="outline" onClick={fetchDashboard} className="mt-4">
            <RefreshCw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </div>
      );
    }

    const t = data.current_tier;
    const usagePercent = t.max_users >= 99999
      ? Math.min(data.active_users, 100)
      : Math.round((data.active_users / t.max_users) * 100);

    return (
      <div className="space-y-8" data-testid="subscription-dashboard">
        {/* Page Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              Billing & Subscription
            </h1>
            <p className="text-muted-foreground mt-1">
              {data.organization_name} {data.cisc ? `(${data.cisc})` : ''}
            </p>
          </div>
          {/* Currency selector */}
          <button
            onClick={fetchAllCurrencies}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-lg border border-border hover:border-[#384752]"
            data-testid="currency-selector-btn"
          >
            <Globe className="w-4 h-4" />
            <span>{currencyInfo?.currency || 'USD'}</span>
          </button>
        </div>

        {/* Currency picker dropdown */}
        {showCurrencyPicker && (
          <Card className="glass-card border-border" data-testid="currency-picker">
            <CardHeader className="pb-2 border-b border-border">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-foreground">Select Currency</CardTitle>
                <button onClick={() => setShowCurrencyPicker(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
              </div>
            </CardHeader>
            <CardContent className="p-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {allCurrencies.map((c) => (
                  <button
                    key={c.code}
                    onClick={() => setCurrency(c.code)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      currencyInfo?.currency === c.code ? 'border-[#FF6F61] bg-[#FF6F61]/5' : 'border-border hover:border-[#384752]'
                    }`}
                    data-testid={`currency-option-${c.code}`}
                  >
                    <p className="text-foreground font-medium text-sm">{c.symbol} {c.code}</p>
                    <p className="text-xs text-muted-foreground truncate">{c.name}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trial Banner */}
        {data.subscription_status === 'trial' && data.trial_end && (
          <Card className="border-2 border-[#A8E6CF] bg-[#A8E6CF]/5" data-testid="trial-banner">
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-[#A8E6CF]/20 flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#A8E6CF]" />
                  </div>
                  <div>
                    <h3 className="text-foreground font-semibold">Free Trial Active</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Your trial ends on {new Date(data.trial_end).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.
                      Subscription tariffs will only apply after your trial period.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {Math.max(0, Math.ceil((new Date(data.trial_end) - new Date()) / (1000 * 60 * 60 * 24)))} days remaining
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!data.trial_payment_added && (
                    <Button
                      onClick={() => handleUpgrade(data.current_tier.tier_id || 'tier_1_10')}
                      className="btn-primary text-sm"
                      disabled={upgrading}
                      data-testid="add-payment-btn"
                    >
                      <CreditCard className="w-4 h-4 mr-1.5" />Add Payment
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={cancelTrial}
                    disabled={cancelling}
                    className="text-sm border-border text-muted-foreground hover:text-foreground"
                    data-testid="cancel-trial-btn"
                  >
                    {cancelling ? 'Cancelling...' : 'Cancel Trial'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Smart Tier Recommendation Banner */}
        {data.tier_recommendation?.show && (
          <Card className={`border-2 ${data.tier_recommendation.urgency === 'high' ? 'border-[#FF6F61] bg-[#FF6F61]/5' : 'border-amber-500 bg-amber-500/5'}`}
            data-testid="tier-recommendation-banner">
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg flex-shrink-0 ${data.tier_recommendation.urgency === 'high' ? 'bg-[#FF6F61]/20' : 'bg-amber-500/20'}`}>
                    <TrendingUp className={`w-5 h-5 ${data.tier_recommendation.urgency === 'high' ? 'text-[#FF6F61]' : 'text-amber-400'}`} />
                  </div>
                  <div>
                    <h3 className="text-foreground font-semibold">
                      {data.tier_recommendation.urgency === 'high' ? 'Plan Limit Reached' : 'Approaching Plan Limit'}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {data.tier_recommendation.message}{' '}
                      Upgrade to <span className="text-[#00A6A0] font-medium">{data.tier_recommendation.next_tier}</span> ({formatPrice(data.tier_recommendation.next_price)}/mo) for more capacity.
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${data.tier_recommendation.urgency === 'high' ? 'bg-[#FF6F61]' : 'bg-amber-400'}`}
                          style={{ width: `${Math.min(100, data.tier_recommendation.usage_percent)}%` }} />
                      </div>
                      <span className="text-[10px] text-muted-foreground">
                        {data.tier_recommendation.active_users}/{data.tier_recommendation.max_users} users ({data.tier_recommendation.usage_percent}%)
                      </span>
                    </div>
                  </div>
                </div>
                <Button className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white flex-shrink-0"
                  onClick={() => {
                    const section = document.querySelector('[data-testid="available-plans"]');
                    if (section) section.scrollIntoView({ behavior: 'smooth' });
                  }}
                  data-testid="upgrade-tier-btn">
                  View Plans
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-card border-border" data-testid="current-plan-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Current Plan</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-[#FF6F61]">{t.name}</p>
              <p className="text-lg font-semibold text-foreground">{formatPrice(t.price)}/mo</p>
              <div className="flex items-center gap-1.5 mt-2 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#A8E6CF]" />
                <span className={data.subscription_status === 'active' ? 'text-[#A8E6CF]' : data.subscription_status === 'trial' ? 'text-[#A8E6CF]' : 'text-amber-400'}>
                  {data.subscription_status === 'active' ? 'Active' : data.subscription_status === 'trial' ? 'Trial' : data.subscription_status === 'cancelled' ? 'Cancelled' : 'Pending'}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-border" data-testid="active-users-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Active Users</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-foreground">{data.active_users}</p>
              <p className="text-xs text-muted-foreground">
                Tier limit: {t.max_users >= 99999 ? '100+' : t.max_users} users
              </p>
              <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(usagePercent, 100)}%`,
                    backgroundColor: usagePercent > 90 ? '#EF4444' : '#A8E6CF',
                  }}
                />
              </div>
              {data.tier_limits?.at_capacity && (
                <p className="text-xs text-amber-400 mt-1.5 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Approaching tier limit
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="glass-card border-border" data-testid="renewal-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Renewal</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-foreground">
                {data.renewal_date
                  ? new Date(data.renewal_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                  : 'Not yet activated'}
              </p>
              <div className="flex items-center gap-3 mt-3">
                <span className={`text-xs font-medium ${data.auto_renew ? 'text-[#A8E6CF]' : 'text-amber-400'}`}>
                  Auto-renew: {data.auto_renew ? 'ON' : 'OFF'}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleRenewal}
                  className="h-7 text-xs border-border text-muted-foreground hover:text-foreground"
                  data-testid="toggle-renewal-btn"
                >
                  {data.auto_renew ? 'Disable' : 'Enable'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tier Change Alert */}
        {data.tier_changed && (
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-3" data-testid="tier-change-alert">
            <TrendingUp className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-400 font-medium text-sm">Tier Adjustment Detected</p>
              <p className="text-xs text-muted-foreground mt-1">
                Based on your current user count ({data.active_users}), your subscription has been adjusted to the <strong className="text-foreground">{t.name}</strong> tier at {formatPrice(t.price)}/month.
              </p>
            </div>
          </div>
        )}

        {/* Pricing Tiers */}
        <div data-testid="available-plans">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
            Available Plans
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {tiers.map((tier) => {
              const isCurrent = t.tier_id === tier.id;
              return (
                <div
                  key={tier.id}
                  className={`p-5 rounded-xl border text-center transition-all ${
                    isCurrent
                      ? 'border-[#FF6F61] bg-[#FF6F61]/5 ring-1 ring-[#FF6F61]/20'
                      : 'border-border glass-card hover:border-muted-foreground/30'
                  }`}
                  data-testid={`tier-card-${tier.id}`}
                >
                  <p className="text-xs text-muted-foreground mb-1">{tier.range} users</p>
                  <p className="text-2xl font-bold text-foreground">{formatPrice(tier.price)}</p>
                  <p className="text-sm text-muted-foreground mb-3">{tier.name}</p>
                  {isCurrent ? (
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-[#FF6F61]/20 text-[#FF6F61]">
                      Current Plan
                    </span>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUpgrade(tier.id)}
                      disabled={upgrading}
                      className="text-xs border-border text-muted-foreground hover:text-foreground"
                      data-testid={`upgrade-${tier.id}`}
                    >
                      {upgrading ? 'Processing...' : 'Select Plan'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Payment History */}
        <div>
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
            HR Compliance Add-on
          </h2>
          <Card className={`border ${data.hr_subscription_active ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'}`} data-testid="hr-addon-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${data.hr_subscription_active ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                    <Shield className={`w-5 h-5 ${data.hr_subscription_active ? 'text-emerald-400' : 'text-amber-400'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">HR Compliance & Deployment Gate</p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      <strong className="text-foreground">£5/user/month</strong> — Access 96 CQC-branded documents, deployment gate, compliance scoring, and evidence management tools.
                    </p>
                    <div className="flex items-center gap-1.5 mt-2">
                      {data.hr_subscription_active ? (
                        <>
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                          <span className="text-xs text-emerald-400 font-medium">Active</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
                          <span className="text-xs text-amber-400 font-medium">Not Subscribed</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {!data.hr_subscription_active && (
                  <Button
                    className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white flex-shrink-0"
                    onClick={async () => {
                      try {
                        await axios.post(`${API_URL}/api/subscriptions/upgrade`, { tier_id: 'hr_compliance' }, { withCredentials: true });
                        toast.success('HR Compliance add-on activated!');
                        fetchDashboard();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to activate HR add-on');
                      }
                    }}
                    data-testid="activate-hr-addon-btn"
                  >
                    Activate Add-on
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payment History */}
        <div>
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">
            Payment History
          </h2>
          {data.payment_history.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground glass-card rounded-xl border border-border">
              <CreditCard className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No payments recorded yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.payment_history.map((p, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-4 rounded-xl glass-card border border-border"
                  data-testid={`payment-row-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center">
                      <CreditCard className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {formatPrice(p.amount || 0)} — {p.payment_method || 'stripe'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(p.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    p.payment_status === 'paid'
                      ? 'bg-[#A8E6CF]/15 text-[#A8E6CF]'
                      : 'bg-amber-500/15 text-amber-400'
                  }`}>
                    {p.payment_status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active Contract Status */}
        {contractStatus?.has_contract && (
          <Card className="border-2 border-[#00A6A0] bg-[#00A6A0]/5" data-testid="contract-status-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-6 h-6 text-[#00A6A0] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-foreground font-semibold text-lg">{contractStatus.plan_label}</h3>
                    <p className="text-sm text-muted-foreground">
                      {contractStatus.free_months} free month(s) included — applied at end of contract
                    </p>
                  </div>
                </div>
                <Button size="sm" variant="outline"
                  className="text-xs border-[#00A6A0]/30 text-[#00A6A0] hover:bg-[#00A6A0]/10 gap-1"
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API_URL}/api/subscription/contract-pdf`, { credentials: 'include' });
                      if (!response.ok) throw new Error('Failed');
                      const blob = await response.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `FendanaCura_Contract.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('Contract PDF downloaded');
                    } catch { toast.error('Failed to download contract PDF'); }
                  }}
                  data-testid="download-contract-pdf">
                  <FileText className="w-3.5 h-3.5" />Download PDF
                </Button>
              </div>
              <div className="grid sm:grid-cols-3 gap-3 mb-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-[10px] text-muted-foreground">Contract Period</p>
                  <p className="text-sm text-foreground font-medium">
                    {contractStatus.contract_start?.slice(0, 10)} to {contractStatus.contract_end?.slice(0, 10)}
                  </p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-[10px] text-muted-foreground">Months Remaining</p>
                  <p className="text-sm text-foreground font-medium">{contractStatus.months_remaining} months</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-[10px] text-muted-foreground">Auto-Renewal</p>
                  <p className={`text-sm font-medium ${contractStatus.auto_renew ? 'text-[#A8E6CF]' : 'text-amber-400'}`}>
                    {contractStatus.auto_renew ? 'Active' : 'Cancelled'}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border text-xs text-muted-foreground">
                <span>Cancellation deadline: {contractStatus.cancellation_deadline?.slice(0, 10)} (3 months notice)</span>
                {contractStatus.auto_renew && contractStatus.can_cancel_now && !contractStatus.cancellation_requested && (
                  <Button size="sm" variant="outline" className="text-xs border-red-500/30 text-red-400"
                    onClick={cancelRenewal} data-testid="cancel-renewal-btn">
                    Cancel Auto-Renewal
                  </Button>
                )}
                {contractStatus.cancellation_requested && (
                  <span className="text-amber-400 font-medium">Cancellation requested</span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contract Plans — show when no active contract */}
        {contractPlans && !contractStatus?.has_contract && (
          <div data-testid="contract-plans-section">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Contract Plans
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              Lock in your rate with a long-term contract and get free months. Minimum 12-month commitment.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              {contractPlans.plans.map(plan => (
                <div key={plan.id} className="p-5 rounded-xl border border-border glass-card hover:border-[#00A6A0]/50 transition-all"
                  data-testid={`contract-plan-${plan.id}`}>
                  <div className="text-center mb-4">
                    <p className="text-lg font-heading font-bold text-foreground">{plan.label}</p>
                    <p className="text-sm text-[#A8E6CF] font-medium mt-1">{plan.free_months} month(s) free</p>
                  </div>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total cost</span>
                      <span className="text-foreground font-medium">{plan.currency_symbol}{plan.total_cost_local.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Effective monthly</span>
                      <span className="text-[#00A6A0] font-medium">{plan.currency_symbol}{plan.monthly_effective_local.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">You save</span>
                      <span className="text-[#A8E6CF] font-medium">{plan.currency_symbol}{plan.savings_local.toFixed(2)}</span>
                    </div>
                  </div>
                  <Button className="w-full bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white"
                    onClick={() => selectContract(plan.id)} disabled={selectingContract}
                    data-testid={`select-contract-${plan.id}`}>
                    {selectingContract ? 'Processing...' : 'Select Plan'}
                  </Button>
                </div>
              ))}
            </div>
            {/* Terms */}
            <Card className="glass-card border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-medium text-foreground flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4 text-muted-foreground" />Contract Terms & Conditions
                </h3>
                <ul className="space-y-1.5 text-xs text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3 h-3 text-[#00A6A0] flex-shrink-0 mt-0.5" />
                    <span>Minimum contract duration is 12 months</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3 h-3 text-[#00A6A0] flex-shrink-0 mt-0.5" />
                    <span>Free months are applied at the end of the contract period</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3 h-3 text-[#00A6A0] flex-shrink-0 mt-0.5" />
                    <span>3 months written notice required to prevent auto-renewal of contract</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3 h-3 text-[#00A6A0] flex-shrink-0 mt-0.5" />
                    <span>Contracts auto-renew at the same duration and rate unless cancelled</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-3 h-3 text-[#00A6A0] flex-shrink-0 mt-0.5" />
                    <span>Subscription tier auto-adjusts if your network size changes</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardLayout>
      {content()}
    </DashboardLayout>
  );
}
